import React, { useEffect, useMemo, useState } from "react";
import { FaGithub, FaLinkedinIn } from "react-icons/fa";
import { FiArrowDown, FiCheck, FiCopy, FiMail, FiMapPin, FiPhone } from "react-icons/fi";
import Reveal from "./Reveal";
import ThemeToggle from "./ThemeToggle";
import { sections } from "../data/navigation";
import {
    CONTACT_EMAIL,
    CONTACT_PHONE,
    GITHUB_HANDLE,
    GITHUB_URL,
    LINKEDIN_NAME,
    LINKEDIN_URL,
    LOCATION,
    MAP_URL,
    PHONE_DISPLAY,
    RESUME_FILENAME,
    RESUME_URL,
} from "../data/profile";
import { TITLE } from "../data/resume";
import { subscribeToScroll } from "../lib/motion";

const contactItems = [
    { name: "Email", icon: FiMail, label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}`, copy: CONTACT_EMAIL },
    { name: "Phone", icon: FiPhone, label: PHONE_DISPLAY, href: `tel:${CONTACT_PHONE}`, copy: CONTACT_PHONE },
    { name: "Location", icon: FiMapPin, label: LOCATION, href: MAP_URL, external: true },
    { name: "LinkedIn", icon: FaLinkedinIn, label: LINKEDIN_NAME, href: LINKEDIN_URL, external: true },
    { name: "GitHub", icon: FaGithub, label: GITHUB_HANDLE, href: GITHUB_URL, external: true },
];

const pad = (n) => String(n).padStart(2, "0");

function useLocalTime(timeZone) {
    const format = useMemo(
        () => new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone }),
        [timeZone]
    );
    const [time, setTime] = useState(() => format.format(new Date()));

    useEffect(() => {
        const id = setInterval(() => setTime(format.format(new Date())), 20000);
        return () => clearInterval(id);
    }, [format]);

    return time;
}

function useActiveSection() {
    const [active, setActive] = useState(sections[0].id);

    useEffect(
        () =>
            subscribeToScroll(() => {
                const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
                if (atBottom) {
                    setActive(sections[sections.length - 1].id);
                    return;
                }
                const marker = window.scrollY + window.innerHeight * 0.3;
                let current = sections[0].id;
                for (const { id } of sections) {
                    const section = document.getElementById(id);
                    if (section && section.offsetTop <= marker) current = id;
                }
                setActive(current);
            }),
        []
    );

    return active;
}

const Sidebar = () => {
    const [copied, setCopied] = useState(null);
    const localTime = useLocalTime("Africa/Kigali");
    const active = useActiveSection();

    const copy = (value, name) => {
        if (navigator.clipboard) navigator.clipboard.writeText(value).catch(() => {});
        setCopied(name);
        setTimeout(() => setCopied((current) => (current === name ? null : current)), 1800);
    };

    return (
        <aside className="no-scrollbar pb-12 pt-10 lg:sticky lg:top-0 lg:flex lg:h-[100dvh] lg:flex-col lg:self-start lg:overflow-y-auto lg:py-10 lg:pr-12 [@media(min-width:1024px)_and_(max-height:820px)]:py-8">
            <Reveal as="h1" variant="lines" className="leading-[0.86]">
                <span className="mask-line">
                    <span className="display text-[20.5vw] sm:text-[17vw] lg:text-[4.55rem] xl:text-[5.3rem]">NIYOBYOSE</span>
                </span>
                <br />
                <span className="mask-line" style={{ "--line": 1 }}>
                    <span className="serif-accent text-[22vw] leading-[0.82] sm:text-[18.5vw] lg:text-[4.95rem] xl:text-[5.75rem]">
                        Paulin<span className="text-accent-text">.</span>
                    </span>
                </span>
            </Reveal>

            <Reveal delay={350}>
                <p className="mt-6 flex items-center gap-3 font-mono text-[13px] [@media(min-width:1024px)_and_(max-height:820px)]:mt-4 uppercase tracking-[0.14em] text-fg">
                    <span aria-hidden="true" className="h-px w-8 bg-accent" />
                    {TITLE}
                </p>
            </Reveal>

            <Reveal as="ul" delay={450} className="mt-8 space-y-0.5 [@media(min-width:1024px)_and_(max-height:820px)]:mt-5" aria-label="Contact details">
                {contactItems.map((item) => (
                    <li key={item.name} className="group flex min-h-[2.25rem] items-center gap-3 [@media(min-width:1024px)_and_(max-height:820px)]:min-h-[2rem]">
                        <item.icon size={15} className="shrink-0 text-accent-text" aria-hidden="true" />
                        <span className="sr-only">{item.name}:</span>
                        <a
                            href={item.href}
                            target={item.external ? "_blank" : undefined}
                            rel={item.external ? "noopener noreferrer" : undefined}
                            className="min-w-0 truncate text-[15px] text-muted transition-colors hover:text-fg"
                        >
                            <span className="link">{item.label}</span>
                        </a>
                        {item.copy && (
                            <button
                                type="button"
                                onClick={() => copy(item.copy, item.name)}
                                aria-label={copied === item.name ? `${item.name} copied` : `Copy ${item.name.toLowerCase()}`}
                                className={`ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-full transition-[opacity,color,background-color] duration-300 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100 ${
                                    copied === item.name ? "bg-accent text-accent-fg opacity-100" : "text-subtle opacity-0 hover:bg-fg/10 hover:text-fg"
                                }`}
                            >
                                {copied === item.name ? <FiCheck size={13} aria-hidden="true" /> : <FiCopy size={13} aria-hidden="true" />}
                            </button>
                        )}
                    </li>
                ))}
            </Reveal>

            <Reveal delay={550} className="mt-8 flex items-center gap-2.5 [@media(min-width:1024px)_and_(max-height:820px)]:mt-5">
                <a href={RESUME_URL} download={RESUME_FILENAME} className="btn btn-primary">
                    <span>Download Resume</span>
                    <FiArrowDown className="btn-arrow btn-arrow-down" aria-hidden="true" />
                </a>
                <span className="hidden lg:block">
                    <ThemeToggle />
                </span>
            </Reveal>

            <nav aria-label="Sections" className="mt-auto hidden pt-10 lg:block [@media(min-width:1024px)_and_(max-height:820px)]:pt-6">
                <ul className="border-t border-fg/10 pt-4">
                    {sections.map((section, index) => {
                        const isActive = active === section.id;
                        return (
                            <li key={section.id}>
                                <a
                                    href={`#${section.id}`}
                                    aria-current={isActive ? "location" : undefined}
                                    className={`group flex items-center gap-3 py-1.5 [@media(min-width:1024px)_and_(max-height:820px)]:py-1 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                                        isActive ? "text-fg" : "text-subtle hover:text-fg"
                                    }`}
                                >
                                    <span className={isActive ? "text-accent-text" : ""}>{pad(index)}</span>
                                    <span
                                        aria-hidden="true"
                                        className={`h-px bg-accent transition-[width] duration-500 ease-out-expo ${
                                            isActive ? "w-8" : "w-3 bg-fg/20 group-hover:w-6"
                                        }`}
                                    />
                                    {section.label}
                                </a>
                            </li>
                        );
                    })}
                </ul>
                <p className="label mt-6 text-subtle [@media(min-width:1024px)_and_(max-height:820px)]:hidden">
                    Local time <span className="text-fg tabular-nums">{localTime}</span> CAT
                </p>
            </nav>
        </aside>
    );
};

export default Sidebar;
